import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Badge,
} from "@mui/material";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";
// import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import OverviewDashboard from "./OverviewDashboard";
import AnalyticsDashboard from "./AnalyticsDashboard";
import SuppliersDirectory from "./SuppliersDirectory";
import SupplierDetail from "./SupplierDetail";
import TalkWithDataModal from "../../ContractDashboard/TalkWithDataModal";
import FilterModal, { FilterValues } from "../FilterModal";
import { RootState, useAppDispatch } from "../../../store";
import {
  fetchAllSupplierData,
  fetchSuppliersDirectory,
  clearSuppliersError,
  setSuppliersFilters,
} from "../../../store/procurementDashboardSlice";
import { SupplierDirectoryItem } from "../../../types/suppliers";
import overviewIcon from "../../../assets/images/overviewImg.svg";
import performanceIcon from "../../../assets/images/performanceImg.svg";
import supplierIcon from "../../../assets/images/supplierIcon1.svg";
import styles from "./suppliersSection.module.scss";

const SuppliersSection = () => {
  const dispatch = useAppDispatch();
  const [dashboardTab, setDashboardTab] = useState(0);
  const [selectedSupplier, setSelectedSupplier] =
    useState<SupplierDirectoryItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const previousSearchQuery = useRef<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Select data from Redux store
  const {
    supplierOverview,
    suppliersWithMostContracts,
    suppliersDirectory,
    suppliersLoading,
    suppliersError,
    suppliersFilters,
  } = useSelector((state: RootState) => state.procurementDashboard);

  // Fetch all supplier data on mount (overview, most contracts, and directory)
  useEffect(() => {
    dispatch(clearSuppliersError());

    if (
      !supplierOverview ||
      !suppliersWithMostContracts ||
      !suppliersDirectory
    ) {
      dispatch(
        fetchAllSupplierData({
          limit: 10,
          offset: 0,
          start_date: suppliersFilters.start_date,
          end_date: suppliersFilters.end_date,
        }),
      );
    }
  }, [
    dispatch,
    supplierOverview,
    suppliersWithMostContracts,
    suppliersDirectory,
  ]);

  // Clear selected supplier when tab changes
  useEffect(() => {
    if (dashboardTab !== 2) {
      setSelectedSupplier(null);
    }
  }, [dashboardTab]);

  const handleViewDetails = useCallback((supplier: SupplierDirectoryItem) => {
    setSelectedSupplier(supplier);
  }, []);

  const handleBackToDirectory = useCallback(() => {
    setSelectedSupplier(null);
  }, []);

  const handleRefresh = useCallback(() => {
    const currentLimit = suppliersDirectory?.pagination.limit || 10;
    const currentOffset = suppliersDirectory?.pagination.offset || 0;
    dispatch(
      fetchAllSupplierData({
        limit: currentLimit,
        offset: currentOffset,
        search: searchQuery,
        start_date: suppliersFilters.start_date,
        end_date: suppliersFilters.end_date,
      }),
    );
  }, [
    dispatch,
    suppliersDirectory?.pagination.limit,
    suppliersDirectory?.pagination.offset,
    searchQuery,
    suppliersFilters.start_date,
    suppliersFilters.end_date,
  ]);

  const handlePageChange = useCallback(
    (limit: number, offset: number) => {
      dispatch(
        fetchSuppliersDirectory({
          limit,
          offset,
          search: searchQuery,
          start_date: suppliersFilters.start_date,
          end_date: suppliersFilters.end_date,
        }),
      );
    },
    [
      dispatch,
      searchQuery,
      suppliersFilters.start_date,
      suppliersFilters.end_date,
    ],
  );

  // Debounced search handler - only calls API when search query actually changes
  useEffect(() => {
    // Check if search query actually changed
    if (previousSearchQuery.current === searchQuery) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // Only search if query is empty (to reset) or has 3+ characters
      if (searchQuery.length === 0 || searchQuery.length >= 3) {
        const currentLimit = suppliersDirectory?.pagination.limit || 10;
        dispatch(
          fetchSuppliersDirectory({
            limit: currentLimit,
            offset: 0,
            search: searchQuery,
            start_date: suppliersFilters.start_date,
            end_date: suppliersFilters.end_date,
          }),
        );
        previousSearchQuery.current = searchQuery;
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [
    searchQuery,
    dispatch,
    suppliersDirectory?.pagination.limit,
    suppliersFilters.start_date,
    suppliersFilters.end_date,
  ]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleApplyFilters = useCallback(
    (newFilters: FilterValues) => {
      dispatch(setSuppliersFilters(newFilters));
      const currentLimit = suppliersDirectory?.pagination.limit || 10;
      dispatch(
        fetchAllSupplierData({
          limit: currentLimit,
          offset: 0,
          search: searchQuery,
          start_date: newFilters.start_date,
          end_date: newFilters.end_date,
        }),
      );
    },
    [dispatch, suppliersDirectory?.pagination.limit, searchQuery],
  );

  const activeFilterCount =
    suppliersFilters.start_date || suppliersFilters.end_date ? 1 : 0;

  const renderDashboardContent = useMemo(() => {
    // Show loading state
    if (suppliersLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      );
    }

    // Show error state
    if (suppliersError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1" color="error">
            {suppliersError}
          </Typography>
        </Box>
      );
    }

    // Show no data state
    if (!supplierOverview) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      );
    }

    // If a supplier is selected, show detail view
    if (selectedSupplier && dashboardTab === 2) {
      return (
        <SupplierDetail
          supplier={selectedSupplier}
          onBack={handleBackToDirectory}
        />
      );
    }

    // Otherwise show the dashboard content
    switch (dashboardTab) {
      case 0:
        return <OverviewDashboard overviewData={supplierOverview} />;
      case 1:
        return (
          <AnalyticsDashboard
            overviewData={supplierOverview}
            suppliersWithMostContracts={
              suppliersWithMostContracts?.suppliers || []
            }
            filters={suppliersFilters}
          />
        );
      case 2:
        return (
          <SuppliersDirectory
            directoryData={suppliersDirectory}
            loading={suppliersLoading}
            error={suppliersError}
            onViewDetails={handleViewDetails}
            onPageChange={handlePageChange}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
          />
        );
      default:
        return <OverviewDashboard overviewData={supplierOverview} />;
    }
  }, [
    suppliersLoading,
    suppliersError,
    supplierOverview,
    selectedSupplier,
    dashboardTab,
    handleBackToDirectory,
    suppliersWithMostContracts,
    suppliersDirectory,
    handleViewDetails,
    handlePageChange,
    searchQuery,
    handleSearchChange,
  ]);

  return (
    <Box className={styles.container}>
      {/* Hide header and tabs when showing supplier detail */}
      {!(selectedSupplier && dashboardTab === 2) && (
        <>
          <Box className={styles.header}>
            <Box className={styles.titleSection}>
              <Typography variant="h4" className={styles.title}>
                Supplier Management
              </Typography>
              <Typography variant="body2" className={styles.subtitle}>
                Due diligence and supplier performance tracking
              </Typography>
            </Box>
            <Box className={styles.headerActions}>
              <Badge badgeContent={activeFilterCount} color="primary">
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  className={styles.actionButton}
                  onClick={() => setFilterModalOpen(true)}
                >
                  Filters
                </Button>
              </Badge>
              {/* <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                className={styles.actionButton}
              >
                Export
              </Button> */}
              <Button
                variant="outlined"
                startIcon={<ChatBubbleOutlineIcon />}
                className={styles.actionButton}
                onClick={() => setModalOpen(true)}
              >
                Talk with Data
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                className={styles.refreshButton}
                onClick={handleRefresh}
                disabled={suppliersLoading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <Box className={styles.dashboardTabs}>
            <Tabs
              value={dashboardTab}
              onChange={(_e, val) => setDashboardTab(val)}
              className={styles.tabs}
              TabIndicatorProps={{
                style: { backgroundColor: "#00d2aa" },
              }}
            >
              <Tab
                icon={
                  <img
                    src={overviewIcon}
                    alt="Overview Dashboard"
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 6,
                      filter:
                        dashboardTab === 0
                          ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                          : "grayscale(100%) opacity(1)",
                    }}
                  />
                }
                label="Overview Dashboard"
                iconPosition="start"
                className={styles.tab}
                sx={{
                  minHeight: "48px",
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "#455391",
                  },
                }}
              />
              <Tab
                icon={
                  <img
                    src={performanceIcon}
                    alt="Performance Dashboard"
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 6,
                      filter:
                        dashboardTab === 1
                          ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                          : "grayscale(100%) opacity(1)",
                    }}
                  />
                }
                label="Analytics Dashboard"
                iconPosition="start"
                className={styles.tab}
                sx={{
                  minHeight: "48px",
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "#455391",
                  },
                }}
              />
              <Tab
                icon={
                  <img
                    src={supplierIcon}
                    alt="Suppliers Directory"
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 6,
                      filter:
                        dashboardTab === 2
                          ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                          : "grayscale(100%) opacity(1)",
                    }}
                  />
                }
                label="Suppliers Directory"
                iconPosition="start"
                className={styles.tab}
                sx={{
                  minHeight: "48px",
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "#455391",
                  },
                }}
              />
            </Tabs>
          </Box>
        </>
      )}

      {renderDashboardContent}
      <TalkWithDataModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        section="suppliers"
        initialFilters={suppliersFilters}
      />
    </Box>
  );
};

export default SuppliersSection;
